import { answerFor, type AnswerColumn } from './customQuestions';

export interface EventSession {
    id: string;
    event_type_id?: string;
    start_time: string | Date;
    end_time: string | Date;
    timezone?: string | null;
    capacity?: number | null;
    status?: string | null;
    booked_count?: number;
    remaining_spots?: number | null;
    is_full?: boolean;
    meeting_url?: string | null;
}

export interface BookingPrefill {
    name: string;
    email: string;
    phone: string;
}

export const isWebinarEventType = (eventType: any): boolean =>
    eventType?.event_kind === 'webinar';

export const upcomingSessions = (
    sessions: EventSession[] | null | undefined,
    now: Date = new Date()
): EventSession[] =>
    (Array.isArray(sessions) ? sessions : [])
        .filter(s => {
            if (!s || s.status === 'cancelled') return false;
            const end = new Date(s.end_time);
            return !isNaN(end.getTime()) && end.getTime() > now.getTime();
        })
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

export const sessionRemainingSpots = (session: EventSession): number | null => {
    if (session.capacity === null || session.capacity === undefined) return null;
    if (typeof session.remaining_spots === 'number') return Math.max(0, session.remaining_spots);
    const booked = typeof session.booked_count === 'number' ? session.booked_count : 0;
    return Math.max(0, Number(session.capacity) - booked);
};

export const isSessionFull = (session: EventSession): boolean => {
    const remaining = sessionRemainingSpots(session);
    return remaining !== null && remaining <= 0;
};

export const sessionSpotsLabel = (session: EventSession): string | null => {
    const remaining = sessionRemainingSpots(session);
    if (remaining === null) return null;
    if (remaining <= 0) return 'Full';
    return `${remaining} slot${remaining === 1 ? '' : 's'} left`;
};

/**
 * The session to pre-select when a webinar has exactly one upcoming session
 * with seats left — the booking page then skips the one-item session list.
 * Returns null otherwise (0 or 2+ sessions, or the only one is full).
 */
export const soleOpenSession = (
    sessions: EventSession[] | null | undefined,
    now: Date = new Date()
): EventSession | null => {
    const upcoming = upcomingSessions(sessions, now);
    return upcoming.length === 1 && !isSessionFull(upcoming[0]) ? upcoming[0] : null;
};

export const parseBookingPrefill = (search: string): BookingPrefill => {
    const empty: BookingPrefill = { name: '', email: '', phone: '' };
    try {
        const params = new URLSearchParams(search || '');
        const pick = (...keys: string[]): string => {
            for (const key of keys) {
                const value = (params.get(key) || '').trim();
                if (value) return value.slice(0, 255);
            }
            return '';
        };
        return {
            name: pick('name', 'invitee_name'),
            email: pick('email', 'invitee_email'),
            phone: pick('phone', 'invitee_phone'),
        };
    } catch {
        return empty;
    }
};

export const splitPhonePrefill = (
    phone: string,
    codes: string[]
): { code: string | null; number: string } => {
    const raw = (phone || '').trim();
    if (!raw) return { code: null, number: '' };
    const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
    const sorted = [...codes].sort((a, b) => b.length - a.length);
    for (const code of sorted) {
        if (withPlus.startsWith(code) && withPlus.length > code.length) {
            return { code, number: withPlus.slice(code.length).replace(/[^\d]/g, '') };
        }
    }
    return { code: null, number: raw.replace(/[^\d]/g, '') };
};


export const formatEventPrice = (
    price: unknown,
    currency?: string | null
): string | null => {
    const num = Number(price);
    if (!Number.isFinite(num) || num <= 0) return null;
    const cur = (currency || 'INR').toUpperCase();
    const rendered = Number.isInteger(num) ? String(num) : num.toFixed(2);
    return cur === 'INR' ? `₹${rendered}` : `${cur} ${rendered}`;
};

export const isPaidEventType = (eventType: any): boolean =>
    formatEventPrice(eventType?.price, eventType?.currency) !== null;


export interface CheckoutWindowLike {
    location: { href: string };
    top?: CheckoutWindowLike | null;
}

export const redirectToCheckout = (
    url: string,
    win: CheckoutWindowLike | null = typeof window === 'undefined'
        ? null
        : (window as unknown as CheckoutWindowLike)
): 'top' | 'self' | 'blocked' => {
    if (!url || !win) return 'blocked';
    try {
        const top = win.top;
        if (top && top !== win) {
            top.location.href = url;
            return 'top';
        }
    } catch {
    }
    win.location.href = url;
    return 'self';
};


export interface RegistrantCsvRow {
    invitee_name?: string | null;
    invitee_email?: string | null;
    invitee_phone?: string | null;
    session_label?: string | null;
    status?: string | null;
    payment_status?: string | null;
    created_at?: string | Date | null;
    answers?: unknown;
}

export const registrantStatusLabel = (
    row: Pick<RegistrantCsvRow, 'status' | 'payment_status'> | null | undefined
): 'Cancelled' | 'Awaiting payment' | 'Registered' => {
    if (row?.status === 'cancelled') return 'Cancelled';
    if (row?.status === 'pending_payment' || row?.payment_status === 'pending') {
        return 'Awaiting payment';
    }
    return 'Registered';
};

export const registrantHoldsSeat = (
    row: Pick<RegistrantCsvRow, 'status' | 'payment_status'> | null | undefined
): boolean => registrantStatusLabel(row) === 'Registered';

export const csvEscape = (value: unknown): string => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const buildRegistrantsCsv = (rows: RegistrantCsvRow[]): string => {
    const header = ['Name', 'Email', 'Phone', 'Session', 'Status', 'Registered At'];
    const lines = (Array.isArray(rows) ? rows : []).map(r => {
        const registered = r.created_at ? new Date(r.created_at) : null;
        return [
            csvEscape(r.invitee_name || ''),
            csvEscape(r.invitee_email || ''),
            csvEscape(r.invitee_phone || ''),
            csvEscape(r.session_label || ''),
            csvEscape(r.status || 'confirmed'),
            csvEscape(registered && !isNaN(registered.getTime()) ? registered.toISOString() : ''),
        ].join(',');
    });
    return [header.join(','), ...lines].join('\r\n');
};

const FIXED_SHEET_HEADERS = ['Name', 'Email', 'Phone', 'Session', 'Status', 'Registered At'];

/** Sheet header per answer column, made unique against the fixed headers and each other. */
export const answerColumnHeaders = (columns: AnswerColumn[]): string[] => {
    const taken = new Set(FIXED_SHEET_HEADERS);
    return columns.map(c => {
        const base = c.label.trim() || 'Question';
        let header = base;
        let n = 2;
        while (taken.has(header)) header = `${base} (${n++})`;
        taken.add(header);
        return header;
    });
};

export const buildRegistrantSheetRows = (
    rows: RegistrantCsvRow[],
    answerColumns: AnswerColumn[] = []
): Record<string, string>[] => {
    const headers = answerColumnHeaders(answerColumns);
    return (Array.isArray(rows) ? rows : []).map(r => {
        const registered = r.created_at ? new Date(r.created_at) : null;
        const row: Record<string, string> = {
            'Name': r.invitee_name || '',
            'Email': r.invitee_email || '',
            'Phone': r.invitee_phone || '',
            'Session': r.session_label || '',
            'Status': registrantStatusLabel(r),
            'Registered At': registered && !isNaN(registered.getTime())
                ? registered.toISOString().replace('T', ' ').slice(0, 19)
                : '',
        };
        answerColumns.forEach((c, i) => { row[headers[i]] = answerFor(r.answers, c); });
        return row;
    });
};

export const filterRegistrants = <T extends RegistrantCsvRow>(rows: T[], query: string): T[] => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
        (r.invitee_name || '').toLowerCase().includes(q) ||
        (r.invitee_email || '').toLowerCase().includes(q) ||
        (r.invitee_phone || '').toLowerCase().includes(q)
    );
};

export const excelSheetName = (label: string, taken: Set<string>, fallback = 'Session'): string => {
    let base = (label || '')
        .replace(/[:\\/?*[\]]/g, '.')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 31) || fallback;
    let name = base;
    let n = 2;
    while (taken.has(name)) {
        const suffix = ` ${n}`;
        name = base.slice(0, 31 - suffix.length) + suffix;
        n += 1;
    }
    taken.add(name);
    return name;
};

export const sessionDateLabel = (session: Pick<EventSession, 'start_time' | 'end_time'>): string => {
    const start = new Date(session.start_time);
    const end = new Date(session.end_time);
    if (isNaN(start.getTime())) return '';
    const date = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const fmtTime = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return isNaN(end.getTime()) ? `${date} ${fmtTime(start)}` : `${date} ${fmtTime(start)} – ${fmtTime(end)}`;
};

export const toDatetimeLocalValue = (value: string | Date | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fromDatetimeLocalValue = (value: string): string | null => {
    if (!value || typeof value !== 'string') return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
};
